'use client';

import React, { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  CheckIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useProfile } from '@/contexts/ProfileContext';
import { Profile } from '@/app/api/profiles';
import { useToast } from '@/hooks/useToast';

const EMOJI_OPTIONS = [
  '🧑', '💼', '🚀', '✍️', '🌱', '📚', '🎨', '🏢', '💡', '🌍',
  '🎯', '📝', '🔬', '🎬', '🏆', '🌟', '🧪', '💻', '🤝', '🦋',
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onProfileSwitched?: (profile: Profile) => void;
  onTriggerOnboarding?: () => void;
  initialViewMode?: ViewMode;
}

type ViewMode = 'list' | 'create' | 'edit';

export default function ProfileManagerModal({
  isOpen,
  onClose,
  onProfileSwitched,
  onTriggerOnboarding,
  initialViewMode = 'list',
}: Props) {
  const { profiles, activeProfile, setActiveProfile, createProfile, updateProfile, deleteProfile } =
    useProfile();
  const { addToast } = useToast();

  const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode);
  const [editingProfile, setEditingProfile] = useState<Profile | null>(null);
  const [formName, setFormName] = useState('');
  const [formEmoji, setFormEmoji] = useState('🧑');
  const [formHandle, setFormHandle] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const openCreate = () => {
    setFormName('');
    setFormEmoji('🧑');
    setFormHandle('');
    setEditingProfile(null);
    setViewMode('create');
  };

  const openEdit = (profile: Profile) => {
    setFormName(profile.name);
    setFormEmoji(profile.avatarEmoji);
    setFormHandle(profile.handle || '');
    setEditingProfile(profile);
    setViewMode('edit');
  };

  const handleBack = () => {
    setViewMode('list');
    setEditingProfile(null);
  };

  const handleClose = () => {
    setViewMode(initialViewMode);
    setEditingProfile(null);
    onClose();
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      addToast('Profile name cannot be empty.', 'error');
      return;
    }
    setSaving(true);
    try {
      if (viewMode === 'create') {
        // Standardize handle if empty
        const handle = formHandle.trim() || formName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        const newProfile = await createProfile(formName.trim(), formEmoji, {}, handle);
        // Switch to the new profile immediately
        setActiveProfile(newProfile);
        onProfileSwitched?.(newProfile);
        handleClose();
        // Delay slightly so the modal closes before the assistant opens
        setTimeout(() => {
          onTriggerOnboarding?.();
        }, 400);
        addToast(`Profile "${formName.trim()}" created! Let's set up your AI persona.`, 'success');
      } else if (viewMode === 'edit' && editingProfile) {
        await updateProfile(editingProfile.id, {
          name: formName.trim(),
          avatarEmoji: formEmoji,
          handle: formHandle.trim()
        });
        handleBack();
        addToast('Profile updated.', 'success');
      }
    } catch (err) {
      addToast('Something went wrong. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (profile: Profile) => {
    setDeletingId(profile.id);
    try {
      await deleteProfile(profile.id);
      addToast(`Profile "${profile.name}" deleted.`, 'success');
    } catch (err: any) {
      addToast(err?.response?.data?.error || 'Failed to delete profile.', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSwitch = (profile: Profile) => {
    setActiveProfile(profile);
    onProfileSwitched?.(profile);
    handleClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  {viewMode !== 'list' && (
                    <button
                      onClick={handleBack}
                      className="text-sm text-gray-500 hover:text-gray-700 font-medium"
                    >
                      ← Back
                    </button>
                  )}
                  <Dialog.Title className="text-base font-semibold text-gray-900">
                    {viewMode === 'list' && 'My Profiles'}
                    {viewMode === 'create' && 'New Profile'}
                    {viewMode === 'edit' && 'Edit Profile'}
                  </Dialog.Title>
                </div>
                <button
                  onClick={handleClose}
                  className="rounded-full p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* List View */}
              {viewMode === 'list' && (
                <div className="p-4">
                  <div className="space-y-2">
                    {profiles.map((profile) => {
                      const isActive = activeProfile?.id === profile.id;
                      return (
                        <div
                          key={profile.id}
                          className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${isActive
                            ? 'border-blue-200 bg-blue-50 ring-1 ring-blue-300'
                            : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                            }`}
                        >
                          {/* Emoji avatar */}
                          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-xl shadow-sm">
                            {profile.avatarEmoji}
                          </div>

                          {/* Name + badge */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm text-gray-900 truncate">
                                {profile.name}
                              </span>
                              {profile.isDefault && (
                                <span className="text-xs text-gray-400 font-normal">Default</span>
                              )}
                            </div>
                            {isActive && (
                              <span className="text-xs text-blue-600 font-medium">Active</span>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {!isActive && (
                              <button
                                onClick={() => handleSwitch(profile)}
                                className="text-xs font-medium text-blue-600 hover:text-blue-700 px-2.5 py-1 rounded-lg hover:bg-blue-50 transition-colors"
                              >
                                Switch
                              </button>
                            )}
                            <button
                              onClick={() => openEdit(profile)}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                              title="Edit"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            {!profile.isDefault && (
                              <button
                                onClick={() => handleDelete(profile)}
                                disabled={deletingId === profile.id}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                                title="Delete"
                              >
                                {deletingId === profile.id ? (
                                  <span className="block h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <TrashIcon className="h-4 w-4" />
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Add profile button */}
                  <button
                    onClick={openCreate}
                    className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border-2 border-dashed border-gray-200 text-sm font-medium text-gray-500 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50/50 transition-all"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add Profile
                  </button>

                  {/* Hint */}
                  <p className="mt-3 text-xs text-gray-400 text-center">
                    Each profile has its own AI persona and content space.
                  </p>
                </div>
              )}

              {/* Create / Edit Form View */}
              {(viewMode === 'create' || viewMode === 'edit') && (
                <div className="p-6 space-y-5">
                  {/* Emoji picker */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pick an icon
                    </label>
                    <div className="grid grid-cols-10 gap-1.5">
                      {EMOJI_OPTIONS.map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          onClick={() => setFormEmoji(emoji)}
                          className={`w-9 h-9 text-xl rounded-xl flex items-center justify-center transition-all ${formEmoji === emoji
                            ? 'bg-blue-100 ring-2 ring-blue-400 scale-110'
                            : 'hover:bg-gray-100'
                            }`}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Name input */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Profile name
                      </label>
                      <input
                        type="text"
                        value={formName}
                        onChange={(e) => {
                          setFormName(e.target.value);
                          // Auto-update handle if it's currently empty or just matches a previous slugified name
                          const slugified = e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
                          if (viewMode === 'create' && (!formHandle || formHandle === formName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''))) {
                            setFormHandle(slugified);
                          }
                        }}
                        onKeyDown={(e) => e.key === 'Enter' && !saving && handleSave()}
                        placeholder="e.g. Personal Blog, Work, Side Project…"
                        autoFocus
                        maxLength={40}
                        className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm text-gray-900 placeholder-gray-400 shadow-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Profile handle (unique ID)
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-400 sm:text-sm">@</span>
                        </div>
                        <input
                          type="text"
                          value={formHandle}
                          onChange={(e) => setFormHandle(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                          onKeyDown={(e) => e.key === 'Enter' && !saving && handleSave()}
                          placeholder="marketing, blog, tech"
                          className="block w-full pl-7 px-3.5 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm text-gray-900 placeholder-gray-400 shadow-sm"
                        />
                      </div>
                      <p className="mt-1 text-[10px] text-gray-400">Used for dedicated content targeting in external widgets.</p>
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                    <div className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-xl shadow-sm">
                      {formEmoji}
                    </div>
                    <span className="font-medium text-sm text-gray-700">
                      {formName || 'Profile name preview'}
                    </span>
                  </div>

                  {viewMode === 'create' && (
                    <div className="flex items-start gap-2 p-3 rounded-xl bg-blue-50 border border-blue-100">
                      <SparklesIcon className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-blue-700">
                        After creating, the AI assistant will open to help you set up the persona for this profile.
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      onClick={handleBack}
                      className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving || !formName.trim()}
                      className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {saving ? (
                        <span className="block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <CheckIcon className="h-4 w-4" />
                      )}
                      {viewMode === 'create' ? 'Create Profile' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
